import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, ChevronDown, FileSignature, FileText, Lock, Plus, RefreshCw, Search, Timer, X } from 'lucide-react';
import {
  HISTORY_KINDS,
  QUOTE_TABS,
  TIME_RANGES,
  type HistoryEntry,
  type HistoryKind,
  type Paginated,
  type Quote,
  type QuoteStatus,
  type QuoteTab,
  type SignatureDocument,
  type TimeRange,
} from '@worki/shared';
import { api, buildQuery, RequestError } from '../lib/api.js';
import { useDebounced } from '../lib/useDebounced.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { sendActionOf } from '../lib/sendAction.js';
import { currentLocale } from '../i18n.js';
import { formatDateTime, formatDuration, money } from '../lib/format.js';
import { RecordedDeliveryChannel, deleteFolderSelections, folderDeliveryKey, FolderDeliveryPreview, FolderDeliveryRow, type FolderHistoryEntry } from '../components/folderDelivery/FolderDelivery.js';
import { ConfirmDialog } from '../components/Modal.js';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { Select } from '../components/ui.js';
import { SelectionActionBar } from '../components/SelectionActionBar.js';
import { togglePageSelection } from '../lib/pageSelection.js';
import { collectPageKeys, applySelectedBatches } from '../lib/bulkSelection.js';
import { ItemActions } from '../components/ItemActions.js';
import { QuoteDocumentDialog } from '../components/QuoteDocument.js';
import { SendQuoteDialog } from '../components/SendQuoteDialog.js';
import { useToast } from '../components/Toast.js';
import { LibraryList } from './library/LibraryList.js';
import { documentNumber, recipientLine } from './signing/parts.js';
import { DocumentDetail } from './signing/Detail.js';
import { useDocumentActions } from './signing/useDocumentActions.js';
import { cn } from '../lib/cn.js';
import { swipeStep } from '../lib/swipe.js';
import { quoteCanEdit } from '../lib/quoteRevision.js';

/** A swipe pages the two views; a held row still belongs to selection. */
export function historySwipeView(view: 'history' | 'library', x: number, y: number, rtl: boolean, heldMs: number): 'history' | 'library' {
  if (heldMs >= 500) return view;
  const direction = swipeStep(x, y, rtl);
  if (view === 'history' && direction === 1) return 'library';
  if (view === 'library' && direction === -1) return 'history';
  return view;
}

/** `POST /bulk/delete` applies row by row and names the rows it refused. */
interface BulkResult {
  deleted: number;
  failed: string[];
}

/*
 * One list, two kinds of row, so a selection has to carry which kind each id
 * belongs to: the two are deleted through different endpoints, and an id on its
 * own cannot say which one to call.
 */
const rowKey = (entry: HistoryEntry): string =>
  entry.kind === 'signature' ? `d:${entry.document.id}` : entry.kind === 'quote' ? `q:${entry.quote.id}` : folderDeliveryKey(entry);

const idsOf = (selection: ReadonlySet<string>, kind: 'q' | 'd'): string[] =>
  [...selection].filter((key) => key.startsWith(`${kind}:`)).map((key) => key.slice(2));

/** One badge per tab, counted over both halves of the merge. */
type Counts = Record<QuoteTab, number>;

const STATUS_CLASS: Record<QuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-info/10 text-info',
  approved: 'bg-success/10 text-success',
  rejected: 'bg-destructive/10 text-destructive',
  expired: 'bg-warning/10 text-warning',
};

/** The signing statuses, in the palette the quote statuses already use. */
const SIGNATURE_STATUS_CLASS: Record<SignatureDocument['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-info/10 text-info',
  viewed: 'bg-warning/10 text-warning',
  signed: 'bg-success/10 text-success',
  // Refused and expired are both endings, and neither is a failure of the
  // system: they read as closed rather than as alarms.
  declined: 'bg-destructive/10 text-destructive',
  expired: 'bg-muted text-muted-foreground',
  cancelled: 'bg-destructive/10 text-destructive',
};

const PAGE_SIZE = 25;

/**
 * QUO-018 / QUO-019 — the send history.
 *
 * Both of the things this product sends out, in one list: quotes and documents
 * put out for signature. What the owner remembers about last Tuesday is that
 * they sent something to somebody, not which of two senders it went through, so
 * making them guess which screen to look on is making them search twice.
 *
 * The rows stay told apart — their own icon, their own numbering, their own
 * actions — because what can be done with a quote and what can be done with a
 * document for signature are not the same set.
 *
 * Kind and range may be linked in the URL. Status starts at All on each visit.
 */
export function QuotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const locale = currentLocale();
  const [params, setParams] = useSearchParams();

  const [tab, setTab] = useState<QuoteTab>('all');
  const range = (
    TIME_RANGES.includes(params.get('range') as TimeRange) ? params.get('range') : 'all'
  ) as TimeRange;
  const kind = (
    HISTORY_KINDS.includes(params.get('kind') as HistoryKind | 'all') ? params.get('kind') : 'all'
  ) as HistoryKind | 'all';
  /*
   * Two screens under one route: what has been sent, and what is kept ready to
   * send. `view` rather than `tab`, which is already spoken for by the quote
   * statuses — and in the URL, so a link can land on either.
   */
  const month = params.get('month') || undefined;
  const year = params.get('year') || undefined;
  const view = params.get('view') === 'library' ? 'library' : 'history';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selection, updateSelection] = useState<Set<string>>(new Set());
  const selectionEpoch = useRef(0);
  const selectionReadContext = useRef('');
  const setSelection = (next: Parameters<typeof updateSelection>[0]) => { selectionEpoch.current++; updateSelection(next); };
  const [selectMode, setSelectMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [documentPreview, setDocumentPreview] = useState<SignatureDocument | null>(null);
  const [preview, setPreview] = useState<Quote | null>(null);
  const [folderPreview, setFolderPreview] = useState<FolderHistoryEntry | null>(null);
  const [sending, setSending] = useState<Quote | null>(null);
  const [confirmDeleteDocument, setConfirmDeleteDocument] = useState<SignatureDocument | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const documentLocationKey = useRef(location.key);
  const consumingDocumentLink = useRef<{ fromKey: string; destination: string } | null>(null);

  useEffect(() => {
    if (documentLocationKey.current === location.key) return;
    const consuming = consumingDocumentLink.current;
    const consumedLink = consuming?.fromKey === documentLocationKey.current
      && consuming.destination === location.pathname + location.search + location.hash;
    documentLocationKey.current = location.key;
    consumingDocumentLink.current = null;
    // Navigation ends a preview, including Back to an identical history URL.
    // Only our own replacement that consumes `open` keeps the newly opened panel.
    if (!consumedLink) { setDocumentPreview(null); setFolderPreview(null); }
  }, [location.key, location.pathname, location.search, location.hash]);

  // A linked document can be outside this history page or its current filters.
  const openId = params.get('open');
  const requestedDocument = useQuery({
    queryKey: ['signatures', 'one', openId],
    queryFn: () => api.get<SignatureDocument>(`/signatures/${encodeURIComponent(openId!)}`),
    enabled: Boolean(openId),
    retry: false,
  });
  useEffect(() => {
    // Only the currently requested id may consume the link. A late response
    // from an earlier link must not replace or reopen the document panel.
    if (!openId || requestedDocument.data?.id !== openId) return;
    setDocumentPreview(requestedDocument.data);
    const next = new URLSearchParams(params);
    next.delete('open');
    consumingDocumentLink.current = {
      fromKey: location.key,
      destination: location.pathname + (next.size ? '?' + next.toString() : '') + location.hash,
    };
    navigate({ search: next.toString(), hash: location.hash }, { replace: true });
  }, [openId, requestedDocument.data, params, navigate, location.key, location.pathname, location.hash]);

  const closeDocument = () => {
    setDocumentPreview(null);
    const run = params.get('agentRun');
    if (run) {
      void queryClient.invalidateQueries({ queryKey: ['agent'] });
      navigate('/agent?run=' + encodeURIComponent(run));
    }
  };

  const filterRef = useRef<HTMLButtonElement>(null);
  const newRef = useRef<HTMLButtonElement>(null);
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const swipeTouch = useRef<{ x: number; y: number; at: number } | null>(null);
  const swipeClick = useRef(0);

  const debouncedSearch = useDebounced(search, 300);
  selectionReadContext.current = JSON.stringify([search, debouncedSearch, tab, range, kind, view, page, month, year]);
  /* UI-11 — the term stays here, in the query key and the debounce above it;
     the box that edits it is drawn by the bar at the top of the screen. */
  usePageSearch(search, (value) => { selectionEpoch.current++; setSearch(value); }, t('quotes.searchPlaceholder'));
  useEffect(() => {
    setPage(1);
    setSelection(new Set());
  }, [debouncedSearch, tab, range, kind, month, year]);
  useEffect(() => () => {
    selectionEpoch.current++;
    if (longPress.current) clearTimeout(longPress.current);
  }, []);

  const setParam = (key: string, value: string) => {
    selectionEpoch.current++;
    if (key === 'tab') { setTab(value as QuoteTab); return; }
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };

  /*
   * One request for the merge, not one per list. Two requests would have to be
   * stitched together here, and page two of a stitch is not page two of either
   * list — rows go missing exactly when there are enough of them to page.
   */
  const list = useQuery({
    queryKey: ['history', { debouncedSearch, tab, range, kind, page, month, year }],
    queryFn: () =>
      api.get<Paginated<HistoryEntry>>(
        `/history${buildQuery({
          month, year,
          q: debouncedSearch,
          tab,
          range,
          kind,
          page,
          pageSize: PAGE_SIZE,
        })}`,
      ),
  });

  /*
   * The badges are counted over the same merge the list is read from, and with
   * the same search and kind applied. They used to come off `/quotes/counts`,
   * which counts quotes and only quotes: a tab could say 3 and open a list of
   * five, because the two documents on it were never in the number.
   */
  const counts = useQuery({
    queryKey: ['history', 'counts', { debouncedSearch, range, kind, month, year }],
    queryFn: () => api.get<Counts>(`/history/counts${buildQuery({ q: debouncedSearch, range, kind, month, year })}`),
  });

  const selectAllMatching = useMutation({
    mutationFn: async (capture: { epoch: number; context: string; q: string; tab: QuoteTab; range: TimeRange; kind: HistoryKind | 'all'; month?: string; year?: string }) =>
      collectPageKeys(async (page) => {
        if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) throw new Error('Selection cancelled');
        return api.get<Paginated<HistoryEntry>>(`/history${buildQuery({ q: capture.q, tab: capture.tab, range: capture.range, kind: capture.kind, month: capture.month, year: capture.year, page, pageSize: 100 })}`);
      }, rowKey),
    onSuccess: (keys, capture) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) return;
      setSelection(new Set(keys)); setSelectMode(keys.length > 0);
    },
    onError: (_error, capture) => {
      if (capture.epoch === selectionEpoch.current && capture.context === selectionReadContext.current) toast.error(t('errors.network'));
    },
  });

  const bulkDelete = useMutation({
    /*
     * Two endpoints for one selection, because the list holds both kinds of
     * row. Sent together rather than one after the other: they do not depend
     * on each other, and a batch that half-succeeded is reported as such
     * either way.
     */
    mutationFn: async (): Promise<BulkResult & { attempted: string[] }> => {
      const attempted = [...selection];
      const quoteIds = idsOf(selection, 'q');
      const documentIds = idsOf(selection, 'd');
      const results = await Promise.all([
        applySelectedBatches(quoteIds, async (ids) => { const result = await api.post<BulkResult>('/quotes/bulk/delete', { ids }); return { ok: result.deleted, failed: result.failed.map(id => `q:${id}`) }; }, 200),
        applySelectedBatches(documentIds, async (ids) => { const result = await api.post<BulkResult>('/signatures/bulk/delete', { ids }); return { ok: result.deleted, failed: result.failed.map(id => `d:${id}`) }; }, 200),
        deleteFolderSelections(attempted.filter(key => key.startsWith('invoice-folder:') || key.startsWith('library-folder:'))),
      ]);
      return {
        attempted,
        deleted: results.reduce((sum, result) => sum + result.ok, 0),
        failed: results.flatMap((result, index) => result.failed.map(id => index === 2 || id.startsWith('q:') || id.startsWith('d:') ? id : `${index === 0 ? 'q' : 'd'}:${id}`)),
      };
    },
    /*
     * The endpoint reports per-row outcomes rather than failing the whole
     * batch. Reporting only the happy path
     * told the user everything was deleted while rows were still on screen.
     */
    onSuccess: (result) => {
      setConfirmDelete(false);
      setSelection(current => new Set([...current].filter(key => !result.attempted.includes(key) || result.failed.includes(key))));
      setSelectMode(result.failed.length > 0);
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      void queryClient.invalidateQueries({ queryKey: ['quote'] });
      void queryClient.invalidateQueries({ queryKey: ['signatures'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      if (result.failed?.length) {
        toast.error(t('common.bulkSomeFailed', { count: result.failed.length }));
      } else {
        toast.success(t('common.deleted'));
      }
    },
    onError: () => {
      setConfirmDelete(false);
      toast.error(t('errors.unknown'));
    },
  });

  const toggleSelect = (key: string) =>
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /*
   * A long press ends in a click — the browser sends one on mouse-up, and a
   * phone synthesises one after the touch. That click landed on the row that
   * had just been selected and toggled it straight back off, so the gesture
   * that starts a selection was also the gesture that emptied it: hold a row,
   * let go, and the bar says nothing is selected. Nothing could be deleted this
   * way, which is what "can't delete anything here" turned out to mean.
   *
   * So the press that opened selection mode swallows its own click.
   */
  const startLongPress = (key: string) => {
    longPressFired.current = false;
    longPress.current = setTimeout(() => {
      longPressFired.current = true;
      setSelectMode(true);
      setSelection(new Set([key]));
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPress.current) clearTimeout(longPress.current);
  };
  /** True while the click that closes a long press is still to come. */
  const swallowedByLongPress = (): boolean => {
    if (!longPressFired.current) return false;
    longPressFired.current = false;
    return true;
  };

  const download = (quote: Quote) => {
    void api.download(`/quotes/${quote.id}/pdf`, `quote-${quote.number}.pdf`).catch(() => {
      toast.error(t('errors.unknown'));
    });
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['history'] });
    void queryClient.invalidateQueries({ queryKey: ['quotes'] });
    void queryClient.invalidateQueries({ queryKey: ['quote'] });
    void queryClient.invalidateQueries({ queryKey: ['signatures'] });
  };
  const lock = useMutation({
    mutationFn: ({ kind, id, locked }: { kind: HistoryKind; id: string; locked: boolean }) =>
      api.post(`/history/${kind}/${id}/lock`, { locked }),
    onSuccess: () => { invalidate(); toast.success(t('history.lockSaved')); },
    onError: () => toast.error(t('errors.unknown')),
  });
  const { resend: resendDocument, remove: removeDocument, copyDialog } = useDocumentActions(invalidate);

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (list.data && page > pages) setPage(pages);
  }, [list.data, page, pages]);

  /* "Select all" is this page of the list, not every row the filter matches:
     what is offered has to be what is on screen, or the count in the bar
     describes rows the person cannot see and did not mean to delete. */
  const pageKeys = rows.map(rowKey);
  const allSelected = pageKeys.length > 0 && pageKeys.every((key) => selection.has(key));

  /** QUO-010 — the tracking figure the reference prints on every sent row. */
  const viewedLabel = (quote: Quote) =>
    quote.viewCount === 0
      ? t('quotes.notViewedShort')
      : t('quotes.viewedFor', { duration: formatDuration(quote.totalViewSeconds) });

  return (
    <div
      className="animate-fade-up max-w-3xl mx-auto"
      onTouchStartCapture={(event) => {
        swipeClick.current = 0;
        swipeTouch.current = null;
        if (event.touches.length !== 1 || (event.target as Element).closest('button, a, input, textarea, select, [role="dialog"]')) return;
        const touch = event.touches[0]!;
        swipeTouch.current = { x: touch.clientX, y: touch.clientY, at: Date.now() };
      }}
      onTouchMoveCapture={() => {
        if (swipeTouch.current) cancelLongPress();
      }}
      onTouchCancelCapture={() => { swipeTouch.current = null; }}
      onTouchEndCapture={(event) => {
        const start = swipeTouch.current;
        swipeTouch.current = null;
        const touch = event.changedTouches[0];
        if (!start || !touch || event.touches.length || longPressFired.current) return;
        const next = historySwipeView(view, touch.clientX - start.x, touch.clientY - start.y, document.documentElement.dir === 'rtl', Date.now() - start.at);
        if (next === view) return;
        cancelLongPress();
        swipeClick.current = Date.now();
        event.preventDefault();
        setParam('view', next);
      }}
      onClickCapture={(event) => {
        if (swipeClick.current && Date.now() - swipeClick.current < 500) {
          swipeClick.current = 0;
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <div className="flex gap-1 mb-3 p-1 rounded-xl bg-muted/60">
        {(['history', 'library'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setParam('view', option)}
            aria-current={view === option}
            className={cn(
              'flex-1 h-9 rounded-lg text-sm transition-colors',
              view === option ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(option === 'history' ? 'history.tabHistory' : 'history.tabLibrary')}
          </button>
        ))}
      </div>

      {view === 'library' && <LibraryList />}

      {view === 'history' && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('history.kindLabel')}</span>
          <Select
            value={kind}
            aria-label={t('history.kindLabel')}
            placeholder={t('history.kind.library-folder')}
            onChange={(value) => {
              if (value === 'invoice-folder') setTab('all');
              setParam('kind', value);
            }}
            className="h-9 w-auto min-w-32 shrink-0 bg-muted text-sm"
          >
            {(['all', 'quote', 'signature', 'invoice-folder'] as const).map((option) => (
              <option key={option} value={option}>{t(`history.kind.${option}`)}</option>
            ))}
          </Select>
        </div>

        {/* Status filter, with the same counts the tabs used to show. */}
        <div className="relative shrink-0">
          <button
            type="button"
            ref={filterRef}
            onClick={() => setFilterOpen((open) => !open)}
            className="h-9 px-3 rounded-lg bg-muted text-sm flex items-center gap-1.5 hover:bg-accent transition-colors"
          >
            {t(`quotes.tab.${tab}`)}
            <ChevronDown
              className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', filterOpen && 'rotate-180')}
            />
          </button>
          {/* Portalled: this page is one `animate-fade-up` block, whose held
              transform makes it a stacking context, and it scrolls inside
              `page-container`. Either one alone would swallow the menu. */}
          <AnchoredMenu
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterRef}
            align="end"
            width={192}
          >
            {QUOTE_TABS.map((option) => (
              <button
                key={option}
                type="button"
                disabled={option !== 'all' && (kind === 'invoice-folder' || kind === 'library-folder')}
                onClick={() => {
                  setParam('tab', option);
                  setFilterOpen(false);
                }}
                className={cn(
                  'w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center justify-between disabled:opacity-40 disabled:cursor-not-allowed',
                  tab === option && 'text-primary font-medium bg-primary/5',
                )}
              >
                <span>{t(`quotes.tab.${option}`)}</span>
                <span className="text-xs text-muted-foreground ltr-nums">{counts.data?.[option] ?? 0}</span>
              </button>
            ))}
            <div className="border-t border-border p-2">
              <span className="block px-2 pb-1 text-[11px] font-semibold text-muted-foreground">
                {t('quotes.rangeLabel')}
              </span>
              <Select
                value={range}
                aria-label={t('quotes.rangeLabel')}
                onChange={(value) => setParam('range', value)}
                className="h-8 px-2 text-xs bg-muted"
              >
                {TIME_RANGES.map((option) => (
                  <option key={option} value={option}>
                    {t(`dashboard.range.${option}`)}
                  </option>
                ))}
              </Select>
            </div>
          </AnchoredMenu>
        </div>

        {/* The way into selection was a half-second press on a row and nothing
            else: a gesture with no affordance, which on a desktop is not a
            gesture anyone has. The press still works; this says it is there. */}
        <button
          type="button"
          onClick={() => {
            setSelectMode((on) => !on);
            setSelection(new Set());
          }}
          aria-pressed={selectMode}
          aria-label={t('common.selectMode')}
          title={t('common.selectMode')}
          className={cn(
            'h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors',
            selectMode ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground hover:bg-accent',
          )}
        >
          <CheckSquare className="h-4 w-4" />
        </button>

        {/* UI-11 — the box that was here is in the bar now, beside the name of
            the screen, and the space it leaves is what keeps this row on one
            line on a phone. */}
        <div className="flex-1" />

        {/* One "+" for a list holding two kinds of row: it asks which, rather
            than picking the one it happens to be named after. */}
        <div className="relative shrink-0">
          <button
            type="button"
            ref={newRef}
            onClick={() => setNewOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={newOpen}
            className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.97]"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('history.new')}</span>
          </button>
          {/* Both rows close the menu as they open their popup. This screen stays
              drawn behind the popup, and its menu with it, over the popup. */}
          <AnchoredMenu open={newOpen} onClose={() => setNewOpen(false)} anchorRef={newRef} align="end" width={200}>
            <button
              type="button"
              onClick={() => {
                setNewOpen(false);
                navigate('/quotes/new');
              }}
              className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
            >
              <FileText className="h-4 w-4 text-muted-foreground" />
              {t('history.newQuote')}
            </button>
            <button
              type="button"
              onClick={() => {
                setNewOpen(false);
                navigate('/signing/new');
              }}
              className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
            >
              <FileSignature className="h-4 w-4 text-muted-foreground" />
              {t('history.newDocument')}
            </button>
          </AnchoredMenu>
        </div>
      </div>

      {selection.size > 0 && (
        <SelectionActionBar count={selection.size} onClear={() => { setSelection(new Set()); setSelectMode(false); }}>
          {/* Ticking twenty rows one at a time is the reason this was asked
              for; the same control gives them all back, so the way out is
              where the way in was. */}
          <button
            type="button"
            onClick={() => setSelection(new Set(togglePageSelection([...selection], pageKeys)))}
            disabled={selectAllMatching.isPending || bulkDelete.isPending}
            className="text-xs text-primary hover:underline"
          >
            {t(allSelected ? 'common.clearSelection' : 'common.selectAll')}
          </button>
          <button type="button" disabled={selectAllMatching.isPending || bulkDelete.isPending || list.isFetching || search !== debouncedSearch} onClick={() => selectAllMatching.mutate({ epoch: ++selectionEpoch.current, context: selectionReadContext.current, q: debouncedSearch, tab, range, kind, month, year })}>
            {t(selectAllMatching.isPending ? 'common.loading' : 'common.selectAllMatching', { count: total })}
          </button>
          <button
            type="button"
            disabled={selection.size === 0 || selectAllMatching.isPending || bulkDelete.isPending}
            onClick={() => setConfirmDelete(true)}
            className="text-xs text-destructive hover:underline disabled:opacity-40 disabled:no-underline"
          >
            {t('common.delete')}
          </button>
        </SelectionActionBar>
      )}

      <div className="space-y-2">
        {rows.map((entry, index) => {
          const resultNumber = (page - 1) * PAGE_SIZE + index + 1;
          if (entry.kind === 'invoice-folder' || entry.kind === 'library-folder') {
            const key = rowKey(entry);
            return <FolderDeliveryRow key={key} entry={entry} number={resultNumber} selectMode={selectMode} selected={selection.has(key)}
              onToggleSelect={() => toggleSelect(key)} onOpen={() => setFolderPreview(entry)}
              onDelete={() => { setSelection(new Set([key])); setConfirmDelete(true); }}
              onPressStart={() => startLongPress(key)} onPressEnd={cancelLongPress} swallowClick={swallowedByLongPress} />;
          }
          if (entry.kind === 'signature') {
            const key = rowKey(entry);
            return (
              <DocumentRow
                key={`${entry.document.id}:${entry.recipient?.key ?? 'legacy'}`}
                document={entry.document}
                recipient={entry.recipient}
                lastChannel={entry.lastChannel}
                resultNumber={resultNumber}
                selectMode={selectMode}
                selected={selection.has(key)}
                onToggleSelect={() => toggleSelect(key)}
                onPressStart={() => startLongPress(key)}
                onPressEnd={cancelLongPress}
                onOpen={() => setDocumentPreview(entry.document)}
                onToggleLock={() => lock.mutate({ kind: 'signature', id: entry.document.id, locked: !entry.document.isLocked })}
                onResend={() => resendDocument.mutate(entry.document)}
                onSendToAnotherSigner={() => navigate(`/signing/new?duplicate=${entry.document.id}`)}
                onDelete={() => setConfirmDeleteDocument(entry.document)}
                swallowClick={swallowedByLongPress}
              />
            );
          }

          if (entry.kind !== 'quote') return null;
          const quote = entry.quote;
          const send = sendActionOf('quote', quote);
          return (
          <div
            key={`${quote.id}:${entry.recipient?.key ?? 'legacy'}`}
            role="button"
            tabIndex={0}
            className="glass-card list-tint rounded-lg px-3 py-3 cursor-pointer hover:border-primary/20 transition-colors"
            onClick={() => {
              if (swallowedByLongPress()) return;
              if (selectMode) toggleSelect(`q:${quote.id}`);
              else setPreview(quote);
            }}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                if (selectMode) toggleSelect(`q:${quote.id}`);
                else setPreview(quote);
              }
            }}
            onMouseDown={() => startLongPress(`q:${quote.id}`)}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onTouchStart={() => startLongPress(`q:${quote.id}`)}
            onTouchEnd={cancelLongPress}
            onTouchCancel={cancelLongPress}
            onTouchMove={cancelLongPress}
          >
            <div className="flex items-center gap-2 mb-1">
              {selectMode && (
                <input
                  type="checkbox"
                  checked={selection.has(`q:${quote.id}`)}
                  onChange={() => toggleSelect(`q:${quote.id}`)}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={t('common.selectRow')}
                  className="rounded border-border h-4 w-4 accent-primary shrink-0"
                />
              )}
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground ltr-nums" aria-label={t('history.resultNumber', { number: resultNumber })}>{resultNumber}.</span>
              <p className="text-sm font-semibold truncate flex-1">
                {entry.recipient?.name || quote.customer?.name || '—'} <span className="text-muted-foreground font-normal">|</span>{' '}
                {/* Named as well as numbered. A bare "7" beside a customer is a
                    number nobody can say out loud; a list that will hold signing
                    documents beside quotes needs each row to say which it is. */}
                <span className="ltr-nums font-normal text-muted-foreground">
                  {t('quotes.numberBadge', { number: String(quote.number).padStart(2, '0') })}
                </span>
              </p>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0',
                  STATUS_CLASS[quote.status],
                )}
              >
                {t(`enum.quoteStatus.${quote.status}`)}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-1.5" dir="ltr">
              <LockIndicator locked={quote.isLocked} />
              <p dir="auto" className="text-xs text-muted-foreground truncate flex-1 text-start">{quote.title}</p>
            </div>

            {entry.recipient && <p dir="auto" className="text-xs text-muted-foreground break-words mb-1.5">{[entry.recipient.phone, entry.recipient.email, entry.recipient.city].filter(Boolean).join(' · ')}</p>}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground ltr-nums">{formatDateTime(entry.recipient?.sentAt ?? quote.sentAt ?? quote.createdAt, locale)}</span><RecordedDeliveryChannel channel={entry.lastChannel}/></div>
                <SendCount count={quote.sendCount} />

                {quote.sentAt && (
                  <span
                    className={cn(
                      'flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap',
                      quote.viewCount > 0 ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <Timer className="h-3 w-3" />
                    {viewedLabel(quote)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-sm font-semibold ltr-nums">
                  {money(quote.totals.total, quote.currency, locale)}
                </span>
                <span onClick={(event) => event.stopPropagation()}>
                  <ItemActions
                    onView={() => setPreview(quote)}
                    viewLabel={t('quotes.viewDocument')}
                    // 1.16 — the same quote for another customer, offered on every
                    // quote whatever its status: opening the copy touches nothing here.
                    onDuplicate={() => navigate(`/quotes/new?duplicate=${quote.id}`)}
                    duplicateLabel={t('quotes.duplicateForAnotherCustomer')}
                    onEdit={quoteCanEdit(quote) ? () => navigate(`/quotes/${quote.id}/edit`) : undefined}
                    {...(send
                      ? {
                          onResend: () => setSending(quote),
                          resendCount: quote.sendCount,
                          // A draft has never gone out, so its menu offers a send, not a
                          // resend; an approved one has nothing left to ask, so a copy.
                          ...(send === 'send' ? { resendLabel: t('common.send') } : {}),
                          ...(send === 'copy' ? { resendLabel: t('history.sendCopy') } : {}),
                        }
                      : {})}
                    onDownload={() => download(quote)}
                    isLocked={quote.isLocked}
                    onToggleLock={() => lock.mutate({ kind: 'quote', id: quote.id, locked: !quote.isLocked })}
                    onDelete={() => { setSelection(new Set([`q:${quote.id}`])); setConfirmDelete(true); }}
                  />
                </span>
              </div>
            </div>
          </div>
          );
        })}

        {list.isLoading && (
          <div className="text-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>
        )}
        {!list.isLoading && rows.length === 0 && (
          <div className="glass-card rounded-xl text-center py-10 px-4 space-y-2">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm font-medium">{t('quotes.emptyTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('quotes.emptyBody')}</p>
            <button
              type="button"
              onClick={() => navigate('/quotes/new')}
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {t('quotes.new')}
            </button>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="text-xs text-muted-foreground ltr-nums">
            {t('common.page')} {page} {t('common.of')} {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((current) => current + 1)}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      </>
      )}

      {openId && requestedDocument.isError && (
        <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          {t(requestedDocument.error instanceof RequestError ? requestedDocument.error.code : 'errors.unknown')}
        </p>
      )}
      {!openId && documentPreview && <DocumentDetail key={documentPreview.id} document={documentPreview} agentReview={Boolean(params.get('agentRun'))} onClose={closeDocument} onChanged={invalidate} />}
      {folderPreview && <FolderDeliveryPreview key={folderDeliveryKey(folderPreview)} entry={folderPreview} onClose={() => setFolderPreview(null)} />}

      <QuoteDocumentDialog
        quote={preview}
        onClose={() => setPreview(null)}
        /* The preview keeps a rule of its own for its send button, and that rule
           still reads an expired quote as sendable; it is only handed a send
           when the list's rule allows one. */
        {...(preview && sendActionOf('quote', preview)
          ? {
              onSend: (quote: Quote) => {
                setPreview(null);
                setSending(quote);
              },
            }
          : {})}
      />

      {sending && (
        <SendQuoteDialog
          open
          quote={sending}
          onClose={() => setSending(null)}
          onSent={invalidate}
        />
      )}

      {/* 1.17 — "send a copy" on a signed document's row. */}
      {copyDialog}

      <ConfirmDialog
        open={confirmDelete}
        title={t('common.delete')}
        /* The row menu deletes one quote and can still say so. A selection is
           counted instead: it may hold documents as well, and naming it after
           the quotes in it would describe half of what is about to go. */
        message={
          [...selection].some(key => key.startsWith('invoice-folder:') || key.startsWith('library-folder:'))
            ? t('history.folderDeleteConfirm', { count: selection.size })
            : selection.size === 1 && idsOf(selection, 'q').length === 1
            ? t('quotes.deleteConfirm')
            : t('history.deleteConfirm', { count: selection.size })
        }
        loading={bulkDelete.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => bulkDelete.mutate()}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteDocument)}
        title={t('signing.deleteTitle')}
        message={t('signing.deleteBody')}
        confirmLabel={t('common.delete')}
        loading={removeDocument.isPending}
        onCancel={() => setConfirmDeleteDocument(null)}
        onConfirm={() =>
          confirmDeleteDocument &&
          removeDocument.mutate(confirmDeleteDocument, { onSettled: () => setConfirmDeleteDocument(null) })
        }
      />
    </div>
  );
}

/**
 * A document for signature, on the row a quote would have taken.
 *
 * Told apart by its own icon, its own numbering and the rail down its leading
 * edge — enough that a glance separates the two without the list turning into
 * two lists with a gap between them. What it cannot borrow is the quote's right
 * hand column: there is no total to print there, so the recipient goes in its
 * place, which is what the reader is actually looking for on this kind of row.
 */
function DocumentRow({
  document,
  recipient,
  lastChannel,
  resultNumber,
  selectMode,
  selected,
  onToggleSelect,
  onPressStart,
  onPressEnd,
  swallowClick,
  onOpen,
  onResend,
  onDelete,
  onToggleLock,
  onSendToAnotherSigner,
}: {
  document: SignatureDocument;
  recipient?: HistoryEntry['recipient'];
  lastChannel?: 'email' | 'whatsapp' | null;
  resultNumber: number;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onPressStart: () => void;
  onPressEnd: () => void;
  swallowClick: () => boolean;
  onOpen: () => void;
  onResend: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  onSendToAnotherSigner: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const send = sendActionOf('document', document);
  const status = recipient?.signedAt ? 'signed' : document.status;
  const toast = useToast();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (swallowClick()) return;
        if (selectMode) onToggleSelect();
        else onOpen();
      }}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          if (selectMode) onToggleSelect(); else onOpen();
        }
      }}
      onMouseDown={onPressStart}
      onMouseUp={onPressEnd}
      onMouseLeave={onPressEnd}
      onTouchStart={onPressStart}
      onTouchEnd={onPressEnd}
      onTouchCancel={onPressEnd}
      onTouchMove={onPressEnd}
      className="glass-card list-tint rounded-lg px-3 py-3 cursor-pointer hover:border-primary/20 transition-colors border-s-2 border-s-primary/50"
    >
      <div className="flex items-center gap-2 mb-1">
        {/* A document is a row on this list like any other, so it is selected
            like any other. It used to be the half of the page that a batch
            could not reach — and on most days it is the larger half. */}
        {selectMode && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('common.selectRow')}
            className="rounded border-border h-4 w-4 accent-primary shrink-0"
          />
        )}
        <FileSignature className="h-4 w-4 text-primary shrink-0" />
        <span className="text-xs text-muted-foreground ltr-nums" aria-label={t('history.resultNumber', { number: resultNumber })}>{resultNumber}.</span>
        <p className="text-sm font-semibold truncate flex-1">
          {recipient?.name || document.recipientName || '—'} <span className="text-muted-foreground font-normal">|</span>{' '}
          <span className="ltr-nums font-normal text-muted-foreground">
            {t('signing.numberBadge', { number: documentNumber(document) })}
          </span>
        </p>
        <span
          className={cn(
            'px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0',
            SIGNATURE_STATUS_CLASS[status],
          )}
        >
          {t(`signing.status.${status}`)}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-1.5" dir="ltr">
        <LockIndicator locked={document.isLocked} />
        <p dir="auto" className="text-xs text-muted-foreground truncate flex-1 text-start">{document.title}</p>
      </div>

      {recipient && <p dir="auto" className="text-xs text-muted-foreground break-words mb-1.5">{[recipient.phone, recipient.email, recipient.city].filter(Boolean).join(' · ')}</p>}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground ltr-nums">{formatDateTime(recipient?.sentAt ?? document.sentAt ?? document.createdAt, locale)}</span><RecordedDeliveryChannel channel={lastChannel}/></div>
          <SendCount count={document.sendCount} />

          {document.sentAt && (
            <span
              className={cn(
                'flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap',
                document.viewCount > 0 ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground',
              )}
            >
              <Timer className="h-3 w-3" />
              {document.viewCount > 0
                ? t('signing.viewedTimes', { count: document.viewCount })
                : t('signing.notViewed')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 min-w-0">
          <span className="text-[11px] text-muted-foreground truncate max-w-[9rem]">{recipient ? [recipient.phone, recipient.email].filter(Boolean).join(' · ') : recipientLine(document)}</span>
          <span onClick={(event) => event.stopPropagation()}>
            <ItemActions
              onView={onOpen}
              viewLabel={t('common.view')}
              {...(send
                ? {
                    onResend,
                    resendCount: document.sendCount,
                    // A draft has never gone out, so its menu offers a send, not a
                    // resend; a signed one has nothing left to ask, so a copy.
                    ...(send === 'send' ? { resendLabel: t('common.send') } : {}),
                    ...(send === 'copy' ? { resendLabel: t('history.sendCopy') } : {}),
                  }
                : {})}
              onDelete={onDelete}
              onDownload={() => void api.download(`/signatures/${document.id}/pdf`, `document-${document.number}.pdf`).catch(() => toast.error(t('errors.unknown')))}
              isLocked={document.isLocked}
              onToggleLock={onToggleLock}
              onSendToAnotherSigner={onSendToAnotherSigner}
            />
          </span>
        </div>
      </div>
    </div>
  );
}

function LockIndicator({ locked }: { locked: boolean }) {
  const { t } = useTranslation();
  if (!locked) return null;
  return <span title={t('history.locked')} aria-label={t('history.locked')}><Lock className="h-4 w-4 text-destructive shrink-0" /></span>;
}

/**
 * 1.11 — how many times a row went out, beside the date it went out. It used to
 * lead the title line, before the row's icon, so a row sent twice started
 * further in than its neighbours and the icons down the list stopped lining up.
 * From the second send: once is what every dated row already says.
 */
function SendCount({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count <= 1) return null;
  return (
    <span
      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap bg-info/10 text-info ltr-nums"
      title={t('history.sendCount', { count })}
      aria-label={t('history.sendCount', { count })}
    >
      <RefreshCw className="h-3 w-3" />
      {count}
    </span>
  );
}
